import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { CategoryChip, ScoreBar, Movement } from "@/components/app/ScoreBits";
import { StatusChip } from "@/components/app/StatusChip";
import { stageLabel } from "@/lib/charts";
import { cn } from "@/lib/utils";
import { ProvisionalTag } from "@/components/fit/Provisional";

const ROW_H = { compact: 40, normal: 56 };
const VIRTUAL_AFTER = 200;

function Cell({ col, r, compact }) {
  switch (col) {
    case "rank": return <span className="flex items-center gap-1 tabular-nums">{r.rank ? `#${r.rank}` : "—"}<Movement rank={r.rank} prev={r.prev_rank} /></span>;
    case "name": return <span className="block truncate font-medium text-ink">{r.name}{!compact && r.email && <span className="block text-xs font-normal text-ink-muted">{r.email}</span>}</span>;
    case "resume_match": return r.resume_match != null ? <ScoreBar value={r.resume_match} compact={compact} />
      : <StatusChip status={r.parse_status === "needs_attention" ? "Failed" : "Processing"} />;
    case "coverage": return r.must_have_total ? <span className="tabular-nums">{r.must_have_found}/{r.must_have_total}</span> : "—";
    case "category": return <CategoryChip category={r.category} overridden={r.overridden} />;
    case "stage": return <span className="text-ink-secondary">{stageLabel(r.stage)}</span>;
    case "fit_score": return <span className="font-display font-semibold tabular-nums" data-testid={`ranked-fit-${r.application_id}`}>{r.fit_score != null ? <>{Number(r.fit_score).toFixed(1)}{r.fit_provisional && <ProvisionalTag testId={`ranked-fit-provisional-${r.application_id}`} />}</> : <span className="font-sans font-normal text-ink-muted">—</span>}</span>;
    case "tags": return <span className="block truncate text-xs text-ink-secondary">{r.tags.slice(0, 3).join(" · ")}</span>;
    case "last_activity": return <span className="text-xs text-ink-muted">{r.last_activity_at ? formatDistanceToNow(new Date(r.last_activity_at), { addSuffix: true }) : "—"}</span>;
    default: return null;
  }
}

const WIDTH = { rank: "w-20", name: "min-w-[180px] flex-1", resume_match: "w-36", coverage: "w-24", category: "w-44", stage: "w-32", fit_score: "w-32", tags: "w-44", last_activity: "w-32" };

export function RankedTable({ rows, cols, labels, selected, setSelected, compact }) {
  const nav = useNavigate();
  const scroller = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const h = compact ? ROW_H.compact : ROW_H.normal;
  const virtual = rows.length > VIRTUAL_AFTER;
  const [start, end] = useMemo(() => {
    if (!virtual) return [0, rows.length];
    const s = Math.max(0, Math.floor(scrollTop / h) - 10);
    return [s, Math.min(rows.length, s + Math.ceil(640 / h) + 20)];
  }, [virtual, scrollTop, h, rows.length]);
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.application_id));
  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  return (
    <div className="panel overflow-hidden" data-testid="ranked-table">
      <div className="flex items-center gap-3 border-b border-line bg-subtle px-4 py-2 text-xs text-ink-muted">
        <Checkbox checked={allOn} onCheckedChange={() => setSelected(allOn ? new Set() : new Set(rows.map((r) => r.application_id)))} data-testid="select-all-checkbox" />
        {cols.map((c) => <span key={c} className={WIDTH[c]}>{labels[c]}</span>)}
      </div>
      <div ref={scroller} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} className={cn(virtual && "max-h-[640px] overflow-y-auto")} data-testid="ranked-scroll">
        <div style={virtual ? { height: rows.length * h, position: "relative" } : undefined}>
          {rows.slice(start, end).map((r, i) => (
            <div key={r.application_id} data-testid={`ranked-row-${r.application_id}`} onClick={() => nav(`/app/candidates/${r.candidate_id}/overview`)}
              style={virtual ? { position: "absolute", top: (start + i) * h, left: 0, right: 0, height: h } : { height: h }}
              tabIndex={0} onKeyDown={(e) => e.key === "Enter" && nav(`/app/candidates/${r.candidate_id}/overview`)}
              className={cn("tl-row flex items-center gap-3 border-b border-line px-4 text-sm", selected.has(r.application_id) && "bg-brand-tint/60")}>
              <span onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(r.application_id)} onCheckedChange={() => toggle(r.application_id)} data-testid={`select-row-${r.application_id}`} /></span>
              {cols.map((c) => <span key={c} className={cn("min-w-0", WIDTH[c])}><Cell col={c} r={r} compact={compact} /></span>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
